import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUsage } from '@/contexts/UsageContext';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  activeIcon: string;
  path: string;
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Home', icon: 'ri-home-5-line', activeIcon: 'ri-home-5-fill', path: '/' },
  { id: 'uploads', label: 'Uploads', icon: 'ri-upload-cloud-2-line', activeIcon: 'ri-upload-cloud-2-fill', path: '/scan/start' },
  { id: 'history', label: 'History', icon: 'ri-time-line', activeIcon: 'ri-time-fill', path: '/history' },
  { id: 'ai', label: 'LAVYN Tutor', icon: 'ri-bard-line', activeIcon: 'ri-bard-fill', path: '/ai-assistant' },
];

const bottomItems: NavItem[] = [
  { id: 'settings', label: 'Profile & settings', icon: 'ri-settings-4-line', activeIcon: 'ri-settings-4-fill', path: '/profile' },
  { id: 'logout', label: 'Log out', icon: 'ri-logout-box-r-line', activeIcon: 'ri-logout-box-r-fill', path: '/' },
];

export default function DesktopSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { isAuthenticated, signOut } = useAuth();
  const { tier } = useUsage();

  const activeId = useMemo(() => {
    const found = navItems.find((item) => item.path === location.pathname);
    if (found) return found.id;
    if (location.pathname === '/profile' || location.pathname.startsWith('/profile/')) return 'settings';
    if (location.pathname === '/scan/start' || location.pathname.startsWith('/scan/')) return 'uploads';
    if (location.pathname.startsWith('/lesson')) return 'lessons';
    if (location.pathname.startsWith('/subject') || location.pathname.startsWith('/topic')) return 'lessons';
    return 'dashboard';
  }, [location.pathname]);

  const sidebarBg = isDark ? 'bg-[#141416]' : 'bg-white';
  const sidebarBorder = isDark ? 'border-white/[0.04]' : 'border-neutral-200/50';
  const textPrimary = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-[#6E6E73]' : 'text-neutral-400';
  const activeBg = isDark ? 'bg-white/[0.08]' : 'bg-neutral-100';
  const activeText = isDark ? 'text-white' : 'text-neutral-900';
  const hoverBg = isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-neutral-50';
  const logoBg = isDark ? 'bg-white' : 'bg-neutral-900';
  const logoText = isDark ? 'text-black' : 'text-white';
  const upgradeBg = isDark ? 'bg-neutral-900' : 'bg-neutral-900';

  const handleNav = (item: NavItem) => {
    if (item.id === 'logout') {
      if (isAuthenticated) {
        signOut();
      }
      navigate('/');
      return;
    }
    navigate(item.path);
  };

  return (
    <aside
      className={`workspace-sidebar hidden lg:flex flex-col fixed left-0 top-0 h-[100dvh] w-[260px] ${sidebarBg} border-r ${sidebarBorder} z-40 transition-colors duration-500`}
    >
      {/* Brand */}
      <div className="px-6 pt-7 pb-6">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${logoBg} flex items-center justify-center`}>
            <span className={`text-xs font-bold ${logoText}`}>L</span>
          </div>
          <div>
            <h1 className={`text-[15px] font-bold ${textPrimary} tracking-tight`}>LAVYN</h1>
            <p className={`text-[11px] font-medium ${textSecondary}`}>Your personal study tutor</p>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <nav className="flex-1 px-4 overflow-y-auto no-scrollbar">
        <p className={`px-3 text-[10px] font-semibold uppercase tracking-wider ${textSecondary} mb-3`}>Menu</p>
        <div className="flex flex-col gap-1">
          {navItems.map((item) => {
            const isActive = activeId === item.id;
            return (
              <button
                key={item.id}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => handleNav(item)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 cursor-pointer w-full text-left ${
                  isActive
                    ? `${activeBg} ${activeText}`
                    : `${textSecondary} ${hoverBg}`
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${isActive ? item.activeIcon : item.icon} text-base`} />
                </div>
                <span className="whitespace-nowrap">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-neutral-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Server-verified plan card */}
        <div className={`mt-6 mx-1 ${upgradeBg} rounded-2xl p-4`}>
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center mb-3">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-vip-crown-line text-white text-sm" />
            </div>
          </div>
          <p className="text-[13px] font-semibold text-white mb-1">
            {tier.tier === 'premium' ? 'Premium plan' : 'Free plan'}
          </p>
          <p className="text-[11px] text-white/50 leading-relaxed mb-3">
            {tier.tier === 'premium'
              ? '12 videos per month, deeper lessons when needed, and no generation advertisements.'
              : 'Complete teacher-quality lessons with standard generation limits.'}
          </p>
          {tier.tier !== 'premium' && (
            <button
              onClick={() => navigate('/profile')}
              className="w-full py-2 rounded-xl bg-white text-neutral-900 text-[12px] font-semibold hover:bg-white/90 transition-colors cursor-pointer"
            >
              View plan
            </button>
          )}
        </div>
      </nav>

      {/* Bottom Nav */}
      <div className="px-4 py-4 border-t border-white/[0.04]">
        <div className="flex flex-col gap-1">
          {bottomItems.filter((item) => item.id !== 'logout' || isAuthenticated).map((item) => (
            <button
              key={item.id}
              aria-current={item.id === 'settings' && activeId === 'settings' ? 'page' : undefined}
              onClick={() => handleNav(item)}
              className={`flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium transition-all duration-200 cursor-pointer w-full text-left ${
                item.id === 'settings' && activeId === 'settings' ? `${activeBg} ${activeText}` : `${textSecondary} ${hoverBg}`
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-base`} />
              </div>
              <span className="whitespace-nowrap">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}